/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */

import {INTERNATIONAL_DIAL_CODES} from '../components/forms/country-list'

// utils
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getLocaleByReference} from './site-utils'
import breakpoints from '../theme/foundations/breakpoints'
import {CLIENT_DEFAULT_LOCATION_EU, CLIENT_DEFAULT_LOCATION_US} from '../constants'
/**
 * Retrieves the application domain based on the environment.
 *
 * This function determines the domain of the application by checking if the code
 * is running in a browser environment or a server environment. In a browser, it
 * returns the origin of the current window location. On the server, it retrieves
 * the domain from the environment variable `PWA_DOMAIN_SITE`, defaulting to
 * 'localhost:3000' if not set. It ensures the domain has a valid protocol prefix.
 *
 * @returns {string} - The application domain, including protocol (e.g., 'https://example.com').
 */
export const getAppDomain = () => {
    if (typeof window !== 'undefined') {
        return window.location.origin
    }

    process.env.PWA_DOMAIN_SITE = process.env.PWA_DOMAIN_SITE || 'localhost:3000'
    const siteDomain = process.env.PWA_DOMAIN_SITE

    return !siteDomain.match(/^[a-zA-Z]+:\/\//)
        ? `https://${process.env.PWA_DOMAIN_SITE}`
        : siteDomain
}

/**
 * Creates a new object by omitting specified keys from the original object.
 *
 * This function iterates over the properties of the provided object and constructs a new object
 * that excludes the specified keys. It is useful for removing unwanted properties from an object.
 *
 * @param {Object} obj - The source object from which keys will be omitted.
 * @param {Array<string>} keys - An array of keys that should be omitted from the source object.
 * @returns {Object} A new object that contains all properties from the source object except those specified in the keys array.
 */
export const omit = (obj, keys) => {
    return Object.keys(obj).reduce((acc, key) => {
        if (!keys.includes(key)) {
            acc[key] = obj[key]
        }
        return acc
    }, {})
}

/**
 * Extracts and returns detailed information from an error stack trace.
 *
 * @param {Error} e - The error object containing the stack trace.
 * @returns {Object} An object containing parsed stack trace information:
 *   - {Array<string>} stackLines - An array of strings, each representing a line in the stack trace.
 *   - {string} firstStackLine - The first line of the stack trace after the error message.
 *   - {string} match - The matched portion of the stack trace line, if any.
 *   - {string} fileName - The name of the file where the error occurred, extracted from the stack trace.
 *   - {string} lineNumber - The line number in the file where the error occurred, extracted from the stack trace.
 */
export const getStackData = (e) => {
    const stackLines = e.stack.split('\n')

    let firstStackLine = stackLines[1]
    let match = ''
    let fileName = ''
    let lineNumber = ''

    if (!firstStackLine) {
        firstStackLine = ''

        return {
            stackLines,
            firstStackLine,
            match,
            fileName,
            lineNumber
        }
    }

    match = firstStackLine.match(/at (.+):(\d+):(\d+)/)
    fileName = match?.[1] || ''
    lineNumber = match?.[2] || ''

    return {
        stackLines,
        firstStackLine,
        match,
        fileName,
        lineNumber
    }
}

/**
 * Fetches available Adyen payment methods for a specified customer and basket.
 *
 * @param {string} customerId - The unique identifier of the customer requesting payment methods.
 * @param {string} token - The authorization token for authenticating the request.
 * @param {string} siteId - The identifier of the site for which payment methods are being requested.
 * @param {string} locale - The locale in which the payment methods should be presented.
 * @param {string} countryCode - The billing country code to filter payment methods.
 * @param {string} basketId - The identifier of the basket for which payment methods are being retrieved.
 * @returns {Promise<Object>} A promise that resolves to an object containing the available Adyen payment methods.
 *
 * @async
 * @throws {Error} Throws an error if the fetch operation fails or if the response cannot be parsed as JSON.
 */
export const getAdyenPaymentMethods = async (
    customerId,
    token,
    siteId,
    locale,
    countryCode,
    basketId
) => {
    const queryParams = new URLSearchParams({
        siteId: siteId,
        locale: locale,
        billingCountryCode: countryCode,
        basketId: basketId
    })

    const response = await fetch('/api/adyen/paymentMethods?' + `${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            customerid: customerId,
            authorization: `Bearer ${token}`
        }
    })

    const result = await response.json()

    return result
}

/**
 * Fetches contact data from the specified API endpoint using the provided request body.
 *
 * This function sends a POST request to the '/api/splio/get-contact' endpoint with the given
 * body as JSON. If the `contact_id` in the body is null, it immediately returns an object
 * indicating failure. Otherwise, it processes the response and returns the result as a JSON object.
 *
 * @param {Object} body - The request payload containing contact information.
 * @param {string|null} body.contact_id - The unique identifier for the contact. If null, the function returns a failure response.
 * @returns {Promise<Object>} A promise that resolves to the response JSON object from the API call. If `contact_id` is null, it resolves to an object with a `success` property set to false.
 */
export const getContactData = async (body) => {
    if (!body.contact_id) {
        return {
            success: false
        }
    }

    const response = await fetch('/api/splio/get-contact', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        ...(body && {
            body: JSON.stringify(body)
        })
    })

    const result = await response.json()

    return result
}

/**
 * Manages and updates cookie consent events in the Google Tag Manager dataLayer.
 *
 * Depending on the provided cookie value and type, this function either updates an existing
 * cookie consent event in the dataLayer or pushes a new event if none exists. It supports
 * both opt-in and opt-out actions for different cookie types and can optionally skip checking
 * for existing events before pushing a new one.
 *
 * @async
 * @param {string} cookieValue - The value indicating the user's cookie consent ('true' or 'false').
 * @param {string} cookieType - The type of cookie being set (e.g., 'analytics', 'marketing').
 * @param {boolean} [shouldCheckExistence=true] - Whether to check for an existing event before pushing a new one.
 * @returns {void}
 */
export const cookieSettings = async (cookieValue, cookieType, shouldCheckExistence = true) => {
    if (window.dataLayer) {
        let cookieEvent = {}
        let cookie = cookieValue == 'false'

        let cookieOptin = 'cookie_' + cookieType + '_optin'
        let cookieOptout = 'cookie_' + cookieType + '_optout'

        cookieEvent.event = cookie ? cookieOptout : cookieOptin
        cookieEvent[cookieOptout] = cookie

        let eventExists = false
        const storegeItemName = 'hasFiredInitialCookieConsentEvent_' + cookieType
        const hasFiredInitialCookieConsentEvent = getSessionJSONItem(storegeItemName)
        for (let i = 0; i < window.dataLayer.length; i++) {
            let dataLayerItem = window.dataLayer[i]

            if (dataLayerItem.event) {
                if (dataLayerItem.event.indexOf('cookie_' + cookieType + '_') > -1) {
                    if (dataLayerItem[cookieOptin]) {
                        delete dataLayerItem[cookieOptin]
                    }

                    if (dataLayerItem[cookieOptout]) {
                        delete dataLayerItem[cookieOptout]
                    }

                    dataLayerItem.event = cookieEvent.event
                    dataLayerItem[cookieEvent.event] = cookie

                    eventExists = true
                    break
                }
            }
        }

        // when events will be fired without checking the existence
        if (shouldCheckExistence === false) {
            window.dataLayer.push(cookieEvent)
            return
        }

        // Push the new event if it doesn't exist
        if (!eventExists && !hasFiredInitialCookieConsentEvent) {
            window.dataLayer.push(cookieEvent)
            setSessionJSONItem(storegeItemName, true)
        }
    }
}

export const requestIdleCallback = (fn) => {
    if ('requestIdleCallback' in window) {
        return window.requestIdleCallback(fn)
    } else {
        return setTimeout(() => fn(), 1)
    }
}

export const watchOnlineStatus = (callback, win = window) => {
    const off = () => callback(false)
    const on = () => callback(true)
    win.addEventListener('offline', off)
    win.addEventListener('online', on)
    const unsubscribe = () => {
        win.removeEventListener('offline', off)
        win.removeEventListener('online', on)
    }
    return unsubscribe
}

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
    for (let key in a) {
        if (!(key in b) || a[key] !== b[key]) {
            return false
        }
    }
    for (let key in b) {
        if (!(key in a) || a[key] !== b[key]) {
            return false
        }
    }
    return true
}

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {}

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
export const flatten = (node, key = 'children') => {
    const children = (node[key] || []).reduce((a, b) => {
        return Array.isArray(b[key]) && !!b[key].length
            ? {...a, ...flatten(b, key)}
            : {...a, [b.id]: b}
    }, {})

    return {
        [node.id]: node,
        ...children
    }
}

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined'

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
    if (isServer) {
        return undefined
    }
    const item = window.sessionStorage.getItem(key)
    if (item) {
        return JSON.parse(item)
    } else {
        return undefined
    }
}
/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
    window.sessionStorage.setItem(key, JSON.stringify(value))
}

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
    window.sessionStorage.removeItem(key)
}

/**
 * Retrieves an item from local storage and parses it as JSON.
 *
 * @param {string} key - The key of the item to retrieve from local storage.
 * @returns {Object|undefined} The parsed JSON object from local storage, or undefined if the item does not exist or if executed on the server.
 */
export const getLocalStorageJSONItem = (key) => {
    if (isServer) {
        return undefined
    }

    const item = window.localStorage.getItem(key)

    if (item && item !== 'undefined') {
        return JSON.parse(item)
    } else {
        return undefined
    }
}

/**
 * Stores a JSON-serializable value in the local storage under the specified key.
 *
 * @param {string} key - The key under which the value will be stored in local storage.
 * @param {Object} value - The JSON-serializable value to be stored.
 */
export const setLocalStorageJSONItem = (key, value) => {
    window.localStorage.setItem(key, JSON.stringify(value))
}

/**
 * Removes an item from the local storage by its key.
 *
 * @param {string} key - The key of the item to be removed from local storage.
 * @returns {void}
 */
export const clearLocalStorageJSONItem = (key) => {
    window.localStorage.removeItem(key)
}

/**
 * escapes special regex characters
 * @param {string} str
 * @returns escaped string
 */
export const escapeRegexChars = (str) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
export const boldString = (str, substr) => {
    return str?.replace(RegExp(escapeRegexChars(substr.trim()), 'gi'), '<b>$&</b>')
}

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
export const capitalize = (text) => {
    return text
        .toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ')
}

const safeToCamel = (str) => {
    if (str.startsWith('_') || str.startsWith('c_')) {
        return str
    }
    return str.replace(/([-_][a-z])/gi, ($1) => {
        return $1[1].toUpperCase()
    })
}

const isPlainObject = (obj) => {
    return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
}

export const keysToCamel = (obj) => {
    if (isPlainObject(obj)) {
        const n = {}

        Object.keys(obj).forEach((k) => {
            n[safeToCamel(k)] = keysToCamel(obj[k])
        })

        return n
    } else if (Array.isArray(obj)) {
        return obj.map(keysToCamel)
    }

    return obj
}

/**
 * Merge two arrays (arr1 and arr2) into one array
 * and merge the matched items (objects with the same id)
 * into one object. There is an assumption where arr2 is
 * always a subset of arr1.
 *
 * @param arr1 - array of objects
 * @param arr2
 * @return {Array}
 */
export const mergeMatchedItems = (arr1 = [], arr2 = []) => {
    const merged = arr1.map((item) => {
        const match = arr2.find((item2) => item2.id === item.id)
        return match ? {...item, ...match} : item
    })
    return merged
}

/**
 * This util func determines if the app is finished hydration
 * @return {boolean}
 */
export const isHydrated = () => typeof window !== 'undefined' && !window.__HYDRATING__

export const isCurrentPageFromPD = (location) => {
    return location?.pathname?.includes('/page/')
}

/**
 * Finds the dial code based on the country code.
 *
 * @param {string} countryCode - The country code to find the dial code for.
 * @returns {string} - The dial code for the given country code.
 */
export const findDialCodeByCountry = (countryCode) => {
    return (
        INTERNATIONAL_DIAL_CODES.find((codeObj) => codeObj.countryCode === countryCode)?.dialCode ??
        ''
    )
}

/**
 * Extracts the country dialing code and the local phone number from a given phone string using a specified regex pattern.
 *
 * @param {RegExp} phoneRegex - The regular expression pattern used to extract the country dialing code and phone number.
 * @param {string} phone - The phone number string to be processed.
 * @returns {Object} An object containing the extracted phone details:
 *   @property {string} countryDial - The extracted country dialing code prefixed with a '+' sign. Returns an empty string if no match is found.
 *   @property {string} phone - The extracted local phone number, trimmed of any leading or trailing whitespace. Returns the original phone string if no match is found.
 *
 * @example
 * // Example 1: Standard format with country code
 *    const result1 = extractPhone('+1 800-555-6789');
 *    { countryDial: '+1', phone: '800-555-6789' }
 *
 * @example
 * // Example 2: No country code
 *    const result2 = extractPhone('123456789');
 *    { countryDial: '', phone: '123456789' }
 */
export const extractPhone = (phoneRegex, phone) => {
    if (!phone) {
        return {
            countryDial: '',
            phone: ''
        }
    }

    const match = phone.match(phoneRegex)

    if (match) {
        return {
            countryDial: `+${match[1]}`,
            phone: match[2]?.trim()
        }
    }

    return {
        countryDial: '',
        phone: phone || ''
    }
}

/**
 * Retrieves the stale time configuration for the application environment.
 *
 * This function extracts the `STALE_TIME` value from the application's configuration.
 * If the `STALE_TIME` is defined, it parses and returns it as an integer. If not defined,
 * it defaults to returning 10,000 milliseconds (10 seconds).
 *
 * @returns {number} The stale time in milliseconds, either from the configuration or the default value.
 */
export const getEnvStaleTime = () => {
    const {app: appConfig} = getConfig()
    let staleTime = 10 * 1000
    try {
        const auxStaleTime = appConfig?.STALE_TIME
        if (
            typeof auxStaleTime === 'number' ||
            (typeof auxStaleTime === 'string' && !isNaN(parseInt(auxStaleTime)))
        ) {
            staleTime = parseInt(auxStaleTime)
        }
    } catch (e) {
        console.warn('Unable to retrieve STALE_TIME from config.')
    }
    return staleTime
}

/**
 * Determines the refetchOnMount behavior for useQuery-based hooks based on environment configuration.
 *
 * This function attempts to retrieve the REFETCH_ON_MOUNT setting from the application
 * configuration. The possible values for this setting are:
 * - true: Refetch on mount is performed if cache is stale. This is the default behavior.
 * - false: Refetch on mount is disabled.
 * - "always": Refetch on mount is always performed.
 *
 * @returns {boolean|string} The refetchOnMount behavior, which can be true, false, or "always".
 */
export const getEnvRefetchOnMount = () => {
    let refetchOnMount = true

    try {
        const {app: appConfig} = getConfig()
        let _refetch = appConfig?.REFETCH_ON_MOUNT

        switch (_refetch) {
            case 'true':
                refetchOnMount = true
                break
            case 'false':
                refetchOnMount = false
                break
            case 'always':
                refetchOnMount = 'always'
                break
        }
    } catch {
        console.warn('Unable to retrieve REFETCH_ON_MOUNT from config.')
    }

    return refetchOnMount
}

/**
 * Toggles the ability to scroll the page by setting the overflow style on the <html> element.
 *
 * @param {boolean} scrollable - A boolean indicating whether the page should be scrollable.
 *                               If true, the page will be scrollable; if false, scrolling will be disabled.
 */
export const togglePageScroll = (scrollable) => {
    document.querySelector('html').style.overflow = scrollable ? '' : 'hidden'
}

/**
 * Adds a specified prefix to the keys of an object.
 *
 * @param {Object} obj - The object whose keys need to be prefixed.
 * @param {string} prefix - The prefix to add to the keys.
 * @returns {Object} A new object with the prefix added to the keys.
 */
export const addPrefixToKeys = (obj, prefix) => {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [`${prefix}${key}`, value]))
}

/**
 * Removes a specified prefix from the keys of an object.
 *
 * @param {Object} obj - The object whose keys need to be renamed.
 * @param {string} prefix - The prefix to remove from the keys.
 * @returns {Object} A new object with the prefix removed from the keys.
 */
export const removePrefixFromKeys = (obj, prefix) => {
    return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
            key.startsWith(prefix) ? key.slice(prefix.length) : key,
            value
        ])
    )
}

/**
 * Determines the level of a category within a parent category tree.
 *
 * This function iterates over the provided parent category tree to find the
 * category with the specified ID and returns its level (1-based index).
 * If the category is not found, it returns -1.
 *
 * @param {string} categoryId - The ID of the category to find.
 * @param {Array<Object>} parentCategoryTree - An array representing the parent category tree,
 *                                             where each element is an object with an 'id' property.
 * @returns {number} The level of the category within the parent category tree (1-based index),
 *                   or -1 if the category is not found.
 */
export const getCategoryLevel = (categoryId, parentCategoryTree) => {
    for (let i = 0; i < parentCategoryTree?.length; i++) {
        if (parentCategoryTree[i]?.id === categoryId) {
            return i + 1
        }
    }
}

/**
 * Generates a sanitized identifier string by converting the input string to lowercase,
 * replacing non-alphanumeric characters with hyphens, and appending optional prefix and suffix.
 *
 * @param {string} str - The input string to be converted into an identifier.
 * @param {string} [prefix=''] - An optional prefix to prepend to the identifier.
 * @param {string} [suffix=''] - An optional suffix to append to the identifier.
 * @returns {string} A sanitized identifier string with the specified prefix and suffix.
 */
export const idFromString = (str, prefix = '', suffix = '') => {
    if (!str) {
        console.error('[idFromString] called with empty string')
        return ''
    }

    let id = str
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')

        // remove duplicate hyphens
        .replace(/-{2,}/g, '-')

        // remove leading and trailing hyphens
        .replace(/(?:^-)|(?:-$)/g, '')

    // Prepend an underscore if the first character is a digit and no prefix is provided
    if (/^\d/.test(id) && !prefix) {
        id = '_' + id
    }

    if (prefix) {
        id = `${prefix}-${id}`
    }

    if (suffix) {
        id = `${id}-${suffix}`
    }

    return id
}

/**
 * Calculates the Haversine distance between two points on the Earth's surface.
 *
 * @param {number} lat1 - Latitude of the first point in degrees.
 * @param {number} lon1 - Longitude of the first point in degrees.
 * @param {number} lat2 - Latitude of the second point in degrees.
 * @param {number} lon2 - Longitude of the second point in degrees.
 * @returns {number} The distance between the two points in kilometers.
 */

export const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180
    const R = 6371 // Radius of the Earth in km
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Distance in km
}

/**
 * Formats a given time string according to the specified locale.
 *
 * @param {string} locale - The locale to use for formatting (e.g., 'en-US', 'fr-FR').
 * @param {string} time - The time string in 24-hour format (e.g., '14:00').
 * @returns {string} - The formatted time string according to the locale's conventions.
 */

export const formatHourByLocale = (locale, time) => {
    if (!time) return null
    const [hours, minutes] = time.split(':')
    const date = new Date()
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10))
    const options = {
        hour: '2-digit',
        minute: '2-digit'
    }

    return new Intl.DateTimeFormat(locale, options).format(date)
}

/**
 * Reports an error to the server by sending a POST request with error details.
 *
 * @param {string} context - A string representing the context in which the error occurred.
 * @param {Error|Object} errorDetails - The error details to report. Can be an Error object or a custom error object.
 * @property {string} [errorDetails.message] - The error message if errorDetails is an Error object.
 * @property {string} [errorDetails.stack] - The stack trace if errorDetails is an Error object.
 * @property {number} [errorDetails.line] - The line number where the error occurred if available.
 * @property {number} [errorDetails.column] - The column number where the error occurred if available.
 *
 * @throws {Error} Throws an error if the network response is not ok.
 */
export const reportError = (context, errorDetails) => {
    const {app: appConfig} = getConfig()
    let errorFormatted = errorDetails
    if (errorDetails instanceof Error) {
        // If it's an Error object, print the stack and message
        errorFormatted = {
            message: JSON.stringify(errorDetails.message),
            stack: errorDetails.stack,
            line: errorDetails.line,
            column: errorDetails.column
        }
    }
    const body = {
        siteId: appConfig.commerceAPI.parameters.siteId,
        context: context,
        error: JSON.stringify(errorFormatted)
    }

    fetch('/api/adyenerror', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    }).then((response) => {
        if (!response.ok) {
            throw new Error('Network response was not ok')
        }
        return response.json()
    })
}

/**
 * Formats a given date according to the specified locale.
 *
 * @param {Date} date - The date to format.
 * @param {string} locale - The locale to use for formatting (e.g., 'en-US', 'fr-FR').
 * @param {Object} options - Optional formatting options.
 * @returns {string} - The formatted date string.
 */
export const formatDateByLocale = (date, locale = 'en-US', options = {}) => {
    const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }
    const formatOptions = {...defaultOptions, ...options}
    const dateToFormat = date instanceof Date ? date : new Date(date)
    return new Intl.DateTimeFormat(locale, formatOptions).format(dateToFormat)
}

/**
 * Retrieves the dimensions (width and height) of a DOM element specified by a selector.
 *
 * @param {string} elementSelector - A CSS selector string used to identify the target DOM element.
 * @returns {Object} An object containing the width and height of the element.
 * If the element is not found, returns an object with both width and height set to 0.
 * @property {number} width - The width of the element in pixels.
 * @property {number} height - The height of the element in pixels.
 */
export const getSizeOfElement = (elementSelector) => {
    const element = document.querySelector(elementSelector)
    if (!element) {
        console.error(`[getSizeOfElement] could not find element with selector ${elementSelector}`)
        return {width: 0, height: 0}
    }

    const {width, height} = element.getBoundingClientRect()
    return {width, height}
}

/**
 * Retrieves a specific region from a page by its region name.
 *
 * This function searches through the regions of a given page and returns
 * the region object that matches the specified region name.
 *
 * @param {Object} page - The page object containing regions.
 * @param {string} regionName - The name of the region to find.
 * @returns {Object|undefined} The region object with the specified name, or undefined if not found.
 */
export const getPageDesignerRegion = (page, regionName) => {
    if (typeof page !== 'object' || 'regions' in page === false) {
        return {components: []}
    }

    return Object.values(page.regions).find((region) => region.id === regionName)
}

/**
 * Localizes a list of options by updating their labels with internationalized messages.
 *
 * @param {Array<Object>} options - An array of option objects, each containing at least a `value` and `label` property.
 * @param {Object} messages - An object containing message definitions for localization, keyed by message identifiers.
 * @param {Object} intl - An internationalization object that provides the `formatMessage` function for message formatting.
 * @returns {Array<Object>} A new array of option objects with localized labels.
 */
export const localizeOptions = (options, messages, intl) => {
    return options.map((option) => ({
        ...option,
        label: intl.formatMessage(
            messages[`state.${option.value.toLowerCase()}`] || {defaultMessage: option.label}
        )
    }))
}

/**
 * Safely parses a JSON string into a JavaScript object.
 * If the parsing fails due to invalid JSON, it returns an empty object instead of throwing an error.
 *
 * @param {string} data - The JSON string to be parsed.
 * @returns {Object} The parsed JavaScript object, or an empty object if parsing fails.
 */
export const safeParseData = (data) => {
    try {
        return JSON.parse(data)
    } catch {
        return {}
    }
}

/**
 * Checks if a given string represents a valid number.
 *
 * This function tests whether the input string is a valid representation of a number,
 * which can be an integer or a floating-point number. It supports both positive and
 * negative numbers.
 *
 * @param {string} value - The string to be tested for numeric validity.
 * @returns {boolean} - Returns true if the string is a valid number, otherwise false.
 */
export const isValidNumberString = (value) => {
    return /^-?\d+(\.\d+)?$/.test(value)
}

/**
 * Creates a map of supported locales from the given site configuration.
 *
 * This function processes a site configuration array to generate a mapping
 * of language codes to their corresponding locale identifiers. Each site
 * in the configuration contains a list of supported locales, and for each
 * locale, the language code is extracted from the locale identifier by
 * splitting it at the hyphen ('-'). The resulting map associates each
 * language code with its full locale identifier.
 *
 * @param {Array} siteConfig - An array of site configuration objects. Each
 *   object should have a `l10n` property containing a `supportedLocales`
 *   array. Each element in `supportedLocales` should be an object with an
 *   `id` property representing the locale identifier (e.g., 'en-US').
 * @returns {Object} A map where the keys are language codes (e.g., 'en')
 *   and the values are the corresponding locale identifiers (e.g., 'en-US').
 */
export const createSupportedLocalesMap = (siteConfig) => {
    const map = {}
    siteConfig.forEach((site) => {
        site.l10n.supportedLocales.forEach((locale) => {
            map[locale.id] = locale.id
        })
    })
    return map
}

/**
 * Determines the closest matching locale for a given site based on client preferences.
 *
 * Each language is first checked for an exact match, and if none is found, it attempts to match based on the language code.
 * If no matching locale was found, it tries with the next one.
 *
 * @param {Object} site - The site object containing localization information.
 * @param {Array<string>} [clientPreferredLocales=navigator.languages] - An array of locale strings
 *     representing the client's preferred locales, defaulting to the browser's language settings.
 * @returns {Object|undefined} - The closest matching locale object from the site, or undefined if no match is found.
 */
export const getClosestLocale = (site, clientPreferredLocales = navigator.languages) => {
    const supportedLocaleIds = site?.l10n.supportedLocales?.map((locale) => locale.id)

    for (const userPreferredLocale of clientPreferredLocales) {
        // try to find an exact match
        if (supportedLocaleIds.includes(userPreferredLocale)) {
            return getLocaleByReference(site, userPreferredLocale)
        }

        // try to find the closest locale by matching the language code
        const languageCode = userPreferredLocale.split('-')[0]
        const closestLocale = supportedLocaleIds.find((locale) => locale.startsWith(languageCode))
        if (closestLocale) {
            return getLocaleByReference(site, closestLocale)
        }
    }
}

/**
 * Generates a formatted HTML string containing detailed error information for debugging purposes.
 * Includes request type, response, request payload, and an error message if provided.
 *
 * @param {Object} data - The error data object.
 * @param {Object} [data.response] - The response object to include in the details (optional).
 * @param {Object} [data.request] - The request object to include in the details (optional).
 * @param {string} [data.errorMessage] - A descriptive error message (optional).
 * @param {string} [data.requestType] - The type of request that was made (optional).
 * @returns {string} An HTML string with formatted error details for display or logging.
 */
export const addErrorDetails = (data) => {
    const {response, request, errorMessage, requestType} = data
    let errorDetails = ''

    if (requestType) {
        errorDetails += `<br><br/><b>Request Type</b>: ${requestType}<br/>`
    }

    if (response) {
        errorDetails += `<br/>Response:<br/><pre style="font-size: 12px">${JSON.stringify(
            response,
            null,
            2
        )}</pre>`
    }

    if (request) {
        errorDetails += `<br/>Request:<br/><pre style="font-size: 12px">${JSON.stringify(
            request,
            null,
            2
        )}</pre>`
    }

    if (errorMessage) {
        errorDetails += `<br/><b>Description</b>: ${errorMessage}`
    }

    return errorDetails
}

/**
 * Generates responsive CSS padding-right rules for a slick-slider component based on breakpoints and slides to show.
 *
 * @param {number} paddingRight - The base padding-right value in pixels.
 * @param {Object} nbSlidesToShow - An object mapping breakpoint keys to the number of slides to show at each breakpoint.
 * @param {Object} breakpoints - An object mapping breakpoint keys to their minimum width values in pixels.
 * @param {number} maxPortion - The divisor for calculating the maximum allowed padding (defaults to 2 if not provided).
 * @returns {string} A concatenated string of CSS media queries for each breakpoint (excluding 'base' and 'sm').
 */
export const generateResponsivePaddingRightForSlider = (
    paddingRight,
    nbSlidesToShow,
    maxPortion
) => {
    return Object.entries(breakpoints)
        .filter(([key]) => !['base', 'sm'].includes(key)) // Exclude the 'base' and 'sm' breakpoint
        .map(([key, value]) => {
            const slidesToShow = nbSlidesToShow[key]
            return `
                @media screen and (min-width: ${parseInt(value, 10)}px) {
                    .slick-slider {
                        padding-right: min(
                            calc(${paddingRight}px + ${paddingRight / 2}px * (${slidesToShow} - 1)),
                            calc(100vw / ${slidesToShow} / ${maxPortion || 2})
                        );
                    }
                }
            `
        })
        .join('\n')
}

/**
 * Retrieves the default client location from the user's settings or preferences.
 * @returns {Object|null} The default location object if available, otherwise null.
 */
export const getClientDefaultLocation = (isUS) => {
    return isUS ? CLIENT_DEFAULT_LOCATION_US : CLIENT_DEFAULT_LOCATION_EU
}
